<template>
    <div
        class="package"
        height="auto"
        :class="{
            selected: selectedItemIds.includes(pkg.id),
            read: isRead,
        }"
    >
        <div class="section" v-if="sections.checkbox.visible">
            <input
                type="checkbox"
                :checked="selectedItemIds.includes(pkg.id)"
                @change="(e) => updateChecked(e.target.checked)"
            />
        </div>
        <div class="section" v-if="sections.view.visible">
            <button class="primary" @click="$router.push(`/office/${pkgLinkMode}/${pkg.id}`)">View</button>
        </div>
        <div
            class="section title grow"
            v-if="sections.title.visible"
            @click="$router.push(`/office/${pkgLinkMode}/${pkg.id}`)"
        >
            <div class="ellipsis">
                <div class="package-name">{{ pkg.title }}</div>
            </div>
            <div class="package-details ellipsis">
                <div v-if="pkg.display_name"><i class="far fa-user"></i>{{ pkg.display_name }}</div>
                <div :title="pkg.last_action">
                    <i class="far fa-clock"></i><span v-if="pkg.last_action">{{ pkg.last_action }} </span
                    >{{ dateFormatLong(pkg.revisionCompressed.submitted_or_created_at) }}
                </div>
            </div>
        </div>

        <template v-for="(pill, index) in infoPills">
            <div
                class="section"
                :class="{ hidden: pill.sectionName === 'tag_data' && index >= sections.tag_data.maxTagsVisible }"
                :key="index"
                v-if="sections[pill.sectionName].visible"
            >
                <div class="pill" :style="pill.styles">
                    <div class="title" :title="pill.title">{{ pill.title }}</div>
                    <div class="description" :title="pill.description">{{ pill.description }}</div>
                </div>
            </div>
        </template>
        <div class="section" v-if="sections.status.visible">
            <div class="status" :style="statusStyle">{{ statusText }}</div>
        </div>

        <div class="section actions" v-if="sections.link.visible || sections.print.visible || sections.archive.visible">
            <span
                v-if="sections.link.visible"
                title="View link"
                class="icon far fa-link clickable"
                :class="{ invisible: !pkg.token }"
                @click="viewLink"
            ></span>
            <span
                v-if="sections.print.visible"
                title="View PDF"
                class="icon far fa-download clickable"
                @click="print"
            ></span>
            <template v-if="sections.archive.visible">
                <span
                    v-if="pkg.is_public"
                    title="Delete public link"
                    class="far fa-trash clickable"
                    @click="$store.dispatch('boxes/deleteOrArchive', [pkg.id])"
                ></span>
                <span
                    v-else-if="pkg.status === 'Draft'"
                    title="Delete item"
                    class="far fa-trash clickable"
                    @click="$store.dispatch('boxes/deleteDrafts', [pkg.id])"
                ></span>
                <template v-else>
                    <span
                        v-if="pkg.is_archived"
                        title="Unarchive item"
                        class="far fa-inbox clickable"
                        @click="
                            $store.dispatch('boxes/archivePkgs', {
                                packageIds: [pkg.id],
                                archive: false,
                            })
                        "
                    ></span>
                    <span
                        v-else
                        title="Archive item"
                        class="far fa-archive clickable"
                        @click="
                            $store.dispatch('boxes/archivePkgs', {
                                packageIds: [pkg.id],
                                archive: true,
                            })
                        "
                    ></span>
                </template>
            </template>
        </div>

        <dialog-window :show="!!displayPkgLink" @closed="displayPkgLink = ''" width="500px" :showX="true">
            <template v-slot:header>
                {{ pkg.title }}
            </template>
            <template v-slot:body>
                <copy-text label="Public Link" :text="displayPkgLink"/>
            </template>
        </dialog-window>
    </div>
</template>
<script>
    import { mapGetters, mapMutations } from 'vuex';
    import { constants } from '../../utils';
    import CopyText from '../CopyText.vue';
    const cloneDeep = require('lodash/cloneDeep');
    const tinycolor = require('tinycolor2');
    const dateFormat = require('dateformat');

    export default {
        name: 'PackageListItem',
        components: { CopyText },
        data() {
            return {
                displayPkgLink: '',
            };
        },
        props: {
            pkg: {
                type: Object,
                required: true,
            },
            index: {
                type: Number,
                required: true,
            },
            box: {
                type: Object,
                required: true,
            },
        },
        computed: {
            ...mapGetters({
                users: 'org/users',
                selectedItemIds: 'boxes/selectedItemIds',
                sections: 'boxes/sections',
                statuses: 'boxes/statuses',
                totalItems: 'boxes/totalItems',
                items: 'boxes/items',
                variables: 'layout/variables',
                buildLink: 'package/buildLink'
            }),
            isPendingWithMe() {
                return this.pkg.status === 'Pending' && (
                    this.pkg.revisionCompressed.sending_to_user_id === this.box.id
                    || this.pkg.revisionCompressed.sending_to_box_id === this.box.id
                );
            },
            statusText() {
                if (this.isPendingWithMe) {
                    return 'Pending with me';
                } else if (this.pkg.is_public && this.pkg.status === 'Pending') {
                    return 'Public';
                }

                return this.pkg.combined_status;
            },
            statusStyle() {
                let color;
                if (this.isPendingWithMe) {
                    color = '#e4f0f9';
                } else {
                    color = this.statuses[this.pkg.combined_status].color;
                }
                return {
                    background: color,
                    borderColor: tinycolor(color).darken(5).toHexString(),
                    color: tinycolor(color).darken(70).toHexString(),
                };
            },
            pkgLinkMode() {
                if (this.isPendingWithMe) return 'edit';
                if (this.pkg.status !== 'Draft') return 'review';
                if (this.pkg.is_public) return 'post';
                return 'send';
            },
            isRead() {
                if (this.pkg.status === 'Pending' && !this.isPendingWithMe) {
                    return true;
                }
                return this.pkg.is_read;
            },
            infoPills() {
                let pills = [];
                let styles = {
                    internal: {
                        backgroundColor: this.variables.colorPackageLabelInternal,
                        borderColor: this.variables.colorPackageLabelInternalDarker,
                    },
                    form: {
                        backgroundColor: this.variables.colorPackageLabelForm,
                        borderColor: this.variables.colorPackageLabelFormDarker,
                    },
                };

                if (this.pkg.labels.length) {
                    for (let label of this.pkg.labels) {
                        pills.push({
                            sectionName: 'tag_data',
                            title: label.value,
                            description: label.name,
                            styles: styles.form,
                        });
                    }
                }

                if (this.pkg.revisionCompressed.sending_to) {
                    pills.push({
                        sectionName: 'sending_to',
                        title: this.pkg.revisionCompressed.sending_to,
                        description: 'Sent To',
                        styles: styles.internal,
                    });
                }

                if (this.pkg.assigned_to_name) {
                    pills.push({
                        sectionName: 'assigned_to_name',
                        title: this.pkg.assigned_to_name,
                        description: 'Assigned To',
                        styles: styles.internal,
                    });
                }

                pills.push({
                    sectionName: 'created_at',
                    title: this.dateFormat(this.pkg.created_at),
                    description: 'Created At',
                    styles: styles.internal,
                });

                return pills;
            },
        },
        methods: {
            ...mapMutations({
                setSelected: 'boxes/setSelected',
            }),
            updateChecked(checked) {
                // if off-page items are selected, unselect them before proceeding
                if (this.selectedItemIds.length === this.totalItems) {
                    let tempIds = cloneDeep(this.selectedItemIds);
                    for (let i = tempIds.length - 1; i >= 0; i--) {
                        let id = tempIds[i];

                        if (!this.items.find((pkg) => pkg.id === id)) {
                            tempIds.splice(i, 1);
                        }
                    }

                    this.$store.commit('boxes/setSelected', tempIds);
                }

                let newIds = cloneDeep(this.selectedItemIds);
                if (!checked && newIds.includes(this.pkg.id)) {
                    newIds.splice(newIds.indexOf(this.pkg.id), 1);
                    this.setSelected(newIds);
                } else if (checked && !newIds.includes(this.pkg.id)) {
                    newIds.push(this.pkg.id);
                    this.setSelected(newIds);
                }
            },
            dateFormat(date, format = constants.LONG_DATE_FORMAT) {
                return dateFormat(date, format);
            },
            dateFormatLong(date) {
                return this.dateFormat(date, constants.LONG_DATE_TIME_FORMAT);
            },
            print() {
                this.$store.dispatch('package/download', {
                    packageId: this.pkg.id,
                    revisionId: this.pkg.revisionCompressed.id,
                });
            },
            viewLink() {
                this.displayPkgLink = this.buildLink(this.pkg, false);
            }
        },
    };
</script>

<style lang="scss" scoped>
    .package {
        display: flex;
        align-items: center;
        margin: 0.5em;
        border: 1px solid lighten($color-disabled-bg, 1);
        @include border-radius-small;
        box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.03);

        &:not(.read) {
            .section.title .package-name {
                font-weight: $bolder;
            }

            .pill .title {
                font-weight: $bold;
            }
        }

        &.selected {
            background-color: $color-select;
            border-color: darken($color-select, 7);
        }

        .section {
            padding: 0.5em 1em;

            &.hidden {
                display: none;
            }

            &:not(.title) {
                flex-shrink: 0;
            }

            &.title {
                min-width: 0;
                flex: 1;
                cursor: pointer;

                .package-details {
                    display: flex;
                    align-items: center;

                    > div:first-child {
                        margin-right: 1em;
                    }

                    i {
                        margin-right: 0.25em;
                    }
                }
            }

            div.ellipsis > * {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            input[type='checkbox'] {
                margin: 0;
            }
        }

        .pill {
            padding: 0.25em 0.5em;
            border-radius: $size-border-radius-large;
            border: 1px solid transparent;
            max-width: 150px;

            .title,
            .description {
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
            }

            .description {
                @extend .info-label;
                text-transform: uppercase;
            }
        }

        &:first-child .selection {
            border-top: none;
        }

        .status {
            width: 85px;
            padding: 0.25em 0.5em;
            text-align: center;
            border-radius: 15px;
            border: 1px solid transparent;
            line-height: 1.1em;
        }

        &.selected .status {
            border-color: initial;
        }

        .info-label {
            font-size: 0.7rem;
        }
    }

    .clickable {
        padding: 0.75em;
        transition: border-color 0.2s;
        border: 1px solid transparent;
        cursor: pointer;
        @include border-radius-smaller;

        &:hover {
            border-color: $color-disabled-bg;
        }
    }
</style>